import type {
  Connection,
  ContentItem,
  ImportBatch,
  NewProduct,
  NewPromotionLink,
  NewSale,
  Platform,
  Product,
  PromotionLink,
  Sale,
  SalesImportInput,
  Storefront,
  WeSellData,
} from "../domain/types";
import { nextShortCode } from "../lib/format";
import { supabase, supabaseConfigured } from "../lib/supabase";
import { seed } from "./seed";

const LOCAL_KEY = "wesell-demo-v1";

function cloneSeed(): WeSellData {
  return structuredClone(seed);
}

function loadLocal(): WeSellData {
  const raw = localStorage.getItem(LOCAL_KEY);
  if (!raw) return cloneSeed();
  try {
    const parsed = JSON.parse(raw) as WeSellData;
    return { ...parsed, imports: parsed.imports ?? [] };
  } catch {
    return cloneSeed();
  }
}

function saveLocal(data: WeSellData): void {
  localStorage.setItem(LOCAL_KEY, JSON.stringify(data));
}

type Row = Record<string, unknown>;

function string(row: Row, key: string, fallback = ""): string {
  return typeof row[key] === "string" ? row[key] : fallback;
}

function number(row: Row, key: string, fallback = 0): number {
  const value = Number(row[key]);
  return Number.isFinite(value) ? value : fallback;
}

function platformFrom(row: Row): Platform {
  return {
    id: string(row, "id"),
    slug: string(row, "slug", "outro") as Platform["slug"],
    name: string(row, "name"),
    referralCode: string(row, "referral_code"),
    affiliateUrl: string(row, "affiliate_url"),
    color: string(row, "color", "#655f73"),
    colorStrong: string(row, "color_strong", "#1e1b2e"),
    connectionStatus: string(
      row,
      "connection_status",
      "disconnected",
    ) as Platform["connectionStatus"],
    connectionMode: string(
      row,
      "connection_mode",
      "report_import",
    ) as Platform["connectionMode"],
    lastSyncAt: (row.last_sync_at as string | null) ?? null,
    nextSyncAt: (row.next_sync_at as string | null) ?? null,
    notes: string(row, "notes"),
  };
}

function productFrom(row: Row): Product {
  return {
    id: string(row, "id"),
    platformId: string(row, "platform_id"),
    shortCode: string(row, "short_code"),
    platformProductId: string(row, "platform_product_id"),
    name: string(row, "name"),
    description: string(row, "description"),
    imageUrl: string(row, "image_url"),
    originalUrl: string(row, "original_url"),
    affiliateUrl: string(row, "affiliate_url"),
    price: row.price === null ? null : number(row, "price"),
    currency: string(row, "currency", "BRL") as Product["currency"],
    category: string(row, "category"),
    status: string(row, "status", "active") as Product["status"],
    published: Boolean(row.published),
    clicks: number(row, "clicks"),
    salesCount: number(row, "sales_count"),
    commissionTotal: number(row, "commission_total"),
    createdAt: string(row, "created_at"),
    updatedAt: string(row, "updated_at"),
  };
}

function contentFrom(row: Row): ContentItem {
  return {
    id: string(row, "id"),
    title: string(row, "title"),
    type: string(row, "type", "outro") as ContentItem["type"],
    channel: string(row, "channel", "Outro") as ContentItem["channel"],
    url: string(row, "url"),
    publishedAt: (row.published_at as string | null) ?? null,
    productIds: Array.isArray(row.product_ids)
      ? (row.product_ids as string[])
      : [],
  };
}

function promotionFrom(row: Row): PromotionLink {
  return {
    id: string(row, "id"),
    platformId: string(row, "platform_id"),
    productId: string(row, "product_id"),
    contentId: (row.content_id as string | null) ?? null,
    url: string(row, "url"),
    trackingId: string(row, "tracking_id"),
    clicks: number(row, "clicks"),
    validated: Boolean(row.validated),
    createdAt: string(row, "created_at"),
  };
}

function saleFrom(row: Row): Sale {
  return {
    id: string(row, "id"),
    platformId: string(row, "platform_id"),
    productId: (row.product_id as string | null) ?? null,
    promotionId: (row.promotion_id as string | null) ?? null,
    externalId: string(row, "external_id"),
    orderDate: string(row, "order_date"),
    grossAmount: number(row, "gross_amount"),
    commissionAmount: number(row, "commission_amount"),
    currency: string(row, "currency", "BRL") as Sale["currency"],
    status: string(row, "status", "pending") as Sale["status"],
    source: string(row, "source", "manual") as Sale["source"],
    importedAt: string(row, "imported_at"),
  };
}

function importFrom(row: Row): ImportBatch {
  return {
    id: string(row, "id"),
    platformId: string(row, "platform_id"),
    fileName: string(row, "file_name"),
    fileFingerprint: string(row, "file_fingerprint"),
    totalRows: number(row, "total_rows"),
    acceptedRows: number(row, "accepted_rows"),
    rejectedRows: number(row, "rejected_rows"),
    warnings: Array.isArray(row.warnings) ? (row.warnings as string[]) : [],
    createdAt: string(row, "created_at"),
  };
}

function connectionFrom(row: Row): Connection {
  return {
    id: string(row, "id"),
    platformId: string(row, "platform_id"),
    mode: string(row, "mode", "report_import") as Connection["mode"],
    status: string(row, "status", "disconnected") as Connection["status"],
    label: string(row, "label"),
    lastSyncAt: (row.last_sync_at as string | null) ?? null,
    nextSyncAt: (row.next_sync_at as string | null) ?? null,
    lastError: string(row, "last_error"),
    fields:
      typeof row.fields === "object" && row.fields
        ? (row.fields as Record<string, string>)
        : {},
  };
}

function storefrontFrom(row: Row | null): Storefront {
  if (!row) return cloneSeed().storefront;
  return {
    slug: string(row, "slug", "laisemesquita"),
    title: string(row, "title", "Achadinhos da Laise"),
    bio: string(row, "bio"),
    disclosure: string(row, "disclosure"),
    active: Boolean(row.active),
  };
}

async function loadRemote(): Promise<WeSellData> {
  const client = supabase();
  const [
    platforms,
    products,
    contents,
    promotions,
    sales,
    imports,
    connections,
    storefront,
  ] = await Promise.all([
    client.from("platforms").select("*").order("name"),
    client
      .from("products")
      .select("*")
      .order("created_at", { ascending: false }),
    client
      .from("content_items_with_products")
      .select("*")
      .order("published_at", { ascending: false }),
    client
      .from("promotion_links")
      .select("*")
      .order("created_at", { ascending: false }),
    client.from("sales").select("*").order("order_date", { ascending: false }),
    client
      .from("import_batches")
      .select("*")
      .order("created_at", { ascending: false }),
    client.from("connections").select("*").order("created_at"),
    client.from("storefronts").select("*").limit(1).maybeSingle(),
  ]);
  const error = [
    platforms,
    products,
    contents,
    promotions,
    sales,
    imports,
    connections,
    storefront,
  ]
    .map((result) => result.error)
    .find(Boolean);
  if (error) throw error;
  return {
    platforms: (platforms.data ?? []).map((row) => platformFrom(row as Row)),
    products: (products.data ?? []).map((row) => productFrom(row as Row)),
    contents: (contents.data ?? []).map((row) => contentFrom(row as Row)),
    promotions: (promotions.data ?? []).map((row) => promotionFrom(row as Row)),
    sales: (sales.data ?? []).map((row) => saleFrom(row as Row)),
    imports: (imports.data ?? []).map((row) => importFrom(row as Row)),
    connections: (connections.data ?? []).map((row) =>
      connectionFrom(row as Row),
    ),
    storefront: storefrontFrom((storefront.data as Row | null) ?? null),
  };
}

export async function loadData(demo: boolean): Promise<WeSellData> {
  return demo || !supabaseConfigured ? loadLocal() : loadRemote();
}

export interface PublicStorefrontData {
  storefront: Storefront;
  products: Product[];
  platforms: Platform[];
}

// Endereços antigos que já circularam em bio de rede social continuam abrindo a
// vitrine mesmo depois da troca de nome: o slug pedido é tentado primeiro e, se
// não existir mais, cai no nome atual.
const SLUGS_ANTIGOS: Record<string, string> = { laisemesquita: "achadinhos" };

async function fetchPublicStorefront(
  slug: string,
): Promise<PublicStorefrontData | null> {
  const { data, error } = await supabase().rpc("get_public_storefront", {
    p_slug: slug,
  });
  if (error) throw error;
  const payload = data as {
    storefront: Row;
    products: Row[];
    platforms: Row[];
  } | null;
  if (!payload) return null;
  return {
    storefront: storefrontFrom(payload.storefront),
    products: payload.products.map(productFrom),
    platforms: payload.platforms.map(platformFrom),
  };
}

export async function loadPublicStorefront(
  slug: string,
): Promise<PublicStorefrontData> {
  const forceDemo =
    new URLSearchParams(window.location.search).get("demo") === "1";
  if (forceDemo || !supabaseConfigured) {
    const local = loadLocal();
    return {
      storefront: local.storefront,
      products: local.products.filter(
        (product) => product.published && product.status === "active",
      ),
      platforms: local.platforms,
    };
  }
  const direct = await fetchPublicStorefront(slug);
  if (direct) return direct;
  const antigo = SLUGS_ANTIGOS[slug.toLowerCase()];
  const renomeada = antigo ? await fetchPublicStorefront(antigo) : null;
  if (renomeada) return renomeada;
  throw new Error("Vitrine não encontrada.");
}

export async function recordPublicClick(
  shortCode: string,
  sessionId: string,
): Promise<void> {
  if (!supabaseConfigured) return;
  const { error } = await supabase().rpc("record_product_click", {
    p_short_code: shortCode,
    p_session_id: sessionId,
  });
  if (error) throw error;
}

export async function createProduct(
  data: WeSellData,
  input: NewProduct,
  demo: boolean,
): Promise<Product> {
  const platform = data.platforms.find((item) => item.id === input.platformId);
  if (!platform) throw new Error("Plataforma inválida.");
  const now = new Date().toISOString();
  const product: Product = {
    ...input,
    id: crypto.randomUUID(),
    shortCode: nextShortCode(
      data.products.map((item) => item.shortCode),
      platform.slug,
    ),
    clicks: 0,
    salesCount: 0,
    commissionTotal: 0,
    createdAt: now,
    updatedAt: now,
  };
  if (demo || !supabaseConfigured) {
    saveLocal({ ...data, products: [product, ...data.products] });
    return product;
  }
  const { data: row, error } = await supabase()
    .from("products")
    .insert({
      platform_id: product.platformId,
      short_code: product.shortCode,
      platform_product_id: product.platformProductId,
      name: product.name,
      description: product.description,
      image_url: product.imageUrl,
      original_url: product.originalUrl,
      affiliate_url: product.affiliateUrl,
      price: product.price,
      currency: product.currency,
      category: product.category,
      status: product.status,
      published: product.published,
    })
    .select("*")
    .single();
  if (error) throw error;
  return productFrom(row as Row);
}

export async function updateProduct(
  data: WeSellData,
  id: string,
  patch: Partial<Product>,
  demo: boolean,
): Promise<Product> {
  const current = data.products.find((item) => item.id === id);
  if (!current) throw new Error("Produto não encontrado.");
  const product = { ...current, ...patch, updatedAt: new Date().toISOString() };
  if (demo || !supabaseConfigured) {
    saveLocal({
      ...data,
      products: data.products.map((item) => (item.id === id ? product : item)),
    });
    return product;
  }
  const { data: row, error } = await supabase()
    .from("products")
    .update({
      name: product.name,
      description: product.description,
      image_url: product.imageUrl,
      original_url: product.originalUrl,
      affiliate_url: product.affiliateUrl,
      platform_product_id: product.platformProductId,
      price: product.price,
      currency: product.currency,
      category: product.category,
      status: product.status,
      published: product.published,
    })
    .eq("id", id)
    .select("*")
    .single();
  if (error) throw error;
  return productFrom(row as Row);
}

export async function createSale(
  data: WeSellData,
  input: NewSale,
  demo: boolean,
): Promise<Sale> {
  const sale: Sale = {
    ...input,
    id: crypto.randomUUID(),
    importedAt: new Date().toISOString(),
  };
  if (demo || !supabaseConfigured) {
    saveLocal({ ...data, sales: [sale, ...data.sales] });
    return sale;
  }
  const { data: row, error } = await supabase()
    .from("sales")
    .upsert(
      {
        platform_id: sale.platformId,
        product_id: sale.productId,
        promotion_id: sale.promotionId,
        external_id: sale.externalId,
        order_date: sale.orderDate,
        gross_amount: sale.grossAmount,
        commission_amount: sale.commissionAmount,
        currency: sale.currency,
        status: sale.status,
        source: sale.source,
      },
      { onConflict: "owner_id,platform_id,external_id" },
    )
    .select("*")
    .single();
  if (error) throw error;
  return saleFrom(row as Row);
}

export async function createContentItem(
  data: WeSellData,
  input: Omit<ContentItem, "id">,
  demo: boolean,
): Promise<ContentItem> {
  const content: ContentItem = { ...input, id: crypto.randomUUID() };
  if (demo || !supabaseConfigured) {
    saveLocal({ ...data, contents: [content, ...data.contents] });
    return content;
  }
  const { data: row, error } = await supabase()
    .from("content_items")
    .insert({
      title: content.title,
      type: content.type,
      channel: content.channel,
      url: content.url,
      published_at: content.publishedAt,
    })
    .select("*")
    .single();
  if (error) throw error;
  if (content.productIds.length) {
    const { error: linkError } = await supabase()
      .from("content_products")
      .insert(
        content.productIds.map((productId) => ({
          content_id: row.id,
          product_id: productId,
        })),
      );
    if (linkError) throw linkError;
  }
  return { ...contentFrom(row as Row), productIds: content.productIds };
}

export async function createPromotionLink(
  data: WeSellData,
  input: NewPromotionLink,
  validated: boolean,
  demo: boolean,
): Promise<PromotionLink> {
  const promotion: PromotionLink = {
    ...input,
    id: crypto.randomUUID(),
    clicks: 0,
    validated,
    createdAt: new Date().toISOString(),
  };
  if (demo || !supabaseConfigured) {
    saveLocal({ ...data, promotions: [promotion, ...data.promotions] });
    return promotion;
  }
  const { data: row, error } = await supabase()
    .from("promotion_links")
    .insert({
      platform_id: promotion.platformId,
      product_id: promotion.productId,
      content_id: promotion.contentId,
      url: promotion.url,
      tracking_id: promotion.trackingId,
      validated: promotion.validated,
    })
    .select("*")
    .single();
  if (error) throw error;
  return promotionFrom(row as Row);
}

export async function importSales(
  data: WeSellData,
  input: SalesImportInput,
  demo: boolean,
): Promise<{ sales: Sale[]; batch: ImportBatch }> {
  const inputs = input.sales;
  const now = new Date().toISOString();
  if (demo || !supabaseConfigured) {
    const existing = new Set(
      data.sales.map((sale) => `${sale.platformId}:${sale.externalId}`),
    );
    const imported = inputs
      .filter(
        (saleInput) =>
          !existing.has(`${saleInput.platformId}:${saleInput.externalId}`),
      )
      .map((saleInput) =>
        Object.assign({}, saleInput, {
          id: crypto.randomUUID(),
          importedAt: new Date().toISOString(),
        }),
      );
    const batch: ImportBatch = {
      id: crypto.randomUUID(),
      platformId: input.platformId,
      fileName: input.fileName,
      fileFingerprint: input.fileFingerprint,
      totalRows: input.totalRows,
      acceptedRows: imported.length,
      rejectedRows: Math.max(0, input.totalRows - imported.length),
      warnings: input.warnings,
      createdAt: now,
    };
    saveLocal({
      ...data,
      sales: [...imported, ...data.sales],
      imports: [batch, ...data.imports],
    });
    return { sales: imported, batch };
  }
  const payload = inputs.map((sale) => ({
    platform_id: sale.platformId,
    product_id: sale.productId,
    promotion_id: sale.promotionId,
    external_id: sale.externalId,
    order_date: sale.orderDate,
    gross_amount: sale.grossAmount,
    commission_amount: sale.commissionAmount,
    currency: sale.currency,
    status: sale.status,
    source: sale.source,
  }));
  const { data: result, error } = await supabase().rpc("import_sales_batch", {
    p_platform_id: input.platformId,
    p_file_name: input.fileName,
    p_file_fingerprint: input.fileFingerprint,
    p_total_rows: input.totalRows,
    p_warnings: input.warnings,
    p_rows: payload,
  });
  if (error) throw error;
  const response = result as { sales: Row[]; batch: Row };
  return {
    sales: (response.sales ?? []).map((row) => saleFrom(row)),
    batch: importFrom(response.batch),
  };
}

export async function updatePlatform(
  data: WeSellData,
  id: string,
  patch: Partial<Platform>,
  demo: boolean,
): Promise<Platform> {
  const current = data.platforms.find((item) => item.id === id);
  if (!current) throw new Error("Plataforma não encontrada.");
  const platform = { ...current, ...patch };
  if (demo || !supabaseConfigured) {
    saveLocal({
      ...data,
      platforms: data.platforms.map((item) =>
        item.id === id ? platform : item,
      ),
    });
    return platform;
  }
  const { data: row, error } = await supabase()
    .from("platforms")
    .update({
      referral_code: platform.referralCode,
      affiliate_url: platform.affiliateUrl,
      notes: platform.notes,
    })
    .eq("id", id)
    .select("*")
    .single();
  if (error) throw error;
  return platformFrom(row as Row);
}

export async function updateStorefront(
  data: WeSellData,
  patch: Partial<Storefront>,
  demo: boolean,
): Promise<Storefront> {
  const storefront = { ...data.storefront, ...patch };
  if (demo || !supabaseConfigured) {
    saveLocal({ ...data, storefront });
    return storefront;
  }
  const { data: row, error } = await supabase()
    .from("storefronts")
    .update({
      slug: storefront.slug,
      title: storefront.title,
      bio: storefront.bio,
      disclosure: storefront.disclosure,
      active: storefront.active,
    })
    .eq("slug", data.storefront.slug)
    .select("*")
    .single();
  if (error) throw error;
  return storefrontFrom(row as Row);
}

export async function saveConnectionSecret(
  platformSlug: string,
  mode: string,
  fields: Record<string, string>,
  demo: boolean,
): Promise<void> {
  if (demo || !supabaseConfigured) return;
  const { error } = await supabase().functions.invoke("connect-platform", {
    body: { platformSlug, mode, fields },
  });
  if (error) throw error;
}

export async function triggerSync(
  platformSlug: string,
  demo: boolean,
): Promise<void> {
  if (demo || !supabaseConfigured) return;
  const { error } = await supabase().functions.invoke("sync-platform", {
    body: { platformSlug },
  });
  if (error) throw error;
}

export function resetDemo(): WeSellData {
  const data = cloneSeed();
  saveLocal(data);
  return data;
}
